import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';
import { requestNotificationPermission } from '@/services/push/notificationPermission';

const NOTIFICATION_ID = 9001;
const CHANNEL_ID = 'ead_downloads';
const NOTIF_TITLE = 'Baixando aulas';

// Estratégia:
// - Android: a notificação RICA (com barra de progresso nativa) vive na ForegroundService
//   do plugin VxlBatchDownloader. O método updateNotification() do plugin atualiza
//   título/corpo/progresso em tempo real.
// - iOS: não tem FGS; usamos @capacitor/local-notifications com o mesmo texto, sem barra.
// - complete()/cancel() são one-shot e usam LocalNotifications nas duas plataformas
//   (no Android, a FGS é encerrada pelo próprio plugin quando o batch acaba).

const MIN_UPDATE_INTERVAL_MS = 500;
let _lastUpdateTime = 0;
let _active = false;
let _initialized = false;
let _initPromise = null;
// Última contagem total reportada. Quando muda (user adiciona aula à fila já
// em andamento), a próxima update() ignora o throttle e atualiza na hora —
// senão o contador "X de Y" demoraria até 500ms pra refletir a mudança.
let _lastTotalCount = 0;
// O que torna a notificação de progresso do iOS não-intrusiva. Sem isto, cada
// re-schedule vira um banner por cima do app — `UNUserNotificationCenter.add`
// dispara apresentação mesmo reusando o identifier.
//
//   silent           → willPresent devolve presentation options vazias, então
//                      com o app aberto nada é mostrado por cima.
//   interruptionLevel → 'passive' entra na lista sem acender a tela nem tocar
//                      som quando o app está em background ou a tela bloqueada.
//
const IOS_QUIET = { silent: true, interruptionLevel: 'passive', sound: null };

// O progresso é sempre silencioso — ninguém quer um banner por tick. Já o
// "Download concluído" é evento único, e se ele deve interromper ou só repousar
// na Central é decisão de produto, não técnica. Flag de BUILD (mesma convenção
// do VUE_APP_ALLOW_SCREEN_CAPTURE) pra dar pra medir os dois nos dois ambientes.
//
// Ausente ou diferente de 'true' → mantém o comportamento atual (banner).
const SILENT_COMPLETE = process.env.VUE_APP_SILENT_DOWNLOAD_COMPLETE === 'true';
// Último corpo publicado. Serve pro refreshTotals() republicar quando SÓ o total
// mudou (usuário enfileirou mais aulas com um download já em curso): sem isso o
// "X de Y" só era recalculado no próximo tick de progresso da aula corrente, e
// numa aula grande o contador ficava minutos desatualizado.
let _lastArgs = { courseName: null, currentName: null, progress: null };
const isAndroid = Capacitor.getPlatform() === 'android';

// Inicialização lazy: a permissão de notificação só é solicitada na primeira
// vez que o user toca em "baixar" — não ao abrir o app. UX melhor: evita
// prompt de permissão no onboarding quando o user nem sabe o que está sendo
// pedido. Idempotente (cached via _initPromise pra evitar race em chamadas
// simultâneas).
async function ensureInitialized () {
  if (!Capacitor.isNativePlatform()) return;
  if (_initialized) return;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    // Permissão pelo serviço único (services/push/notificationPermission): o
    // mesmo que o push remoto usa. Ele já não reabre prompt em `denied` e
    // serializa pedidos concorrentes — o download e o pedido contextual do push
    // não empilham dois prompts do SO.
    await requestNotificationPermission();

    if (isAndroid) {
      // Canal pras notificações one-shot (complete) — diferente do canal da FGS.
      await LocalNotifications.createChannel({
        id: CHANNEL_ID,
        name: 'Downloads de aulas',
        description: 'Progresso de download de aulas offline',
        importance: 2,
        visibility: 1,
        vibration: false,
        sound: null
      });
    }
    _initialized = true;
  })();

  return _initPromise;
}

export const downloadNotification = {
  // Mantida pra retrocompat (caller antigo que ainda invocar). Hoje é alias
  // pra ensureInitialized; pode ser removida quando confirmar que nada chama.
  async init () {
    return ensureInitialized();
  },

  async start (name) {
    if (!Capacitor.isNativePlatform()) return;
    // Lazy init: pede permissão no primeiro download em vez do boot do app.
    // Se user negar, schedule() abaixo falha silenciosamente — download segue.
    await ensureInitialized();

    // Guard COMUM iOS+Android: quando já há fila ativa, start é no-op. O texto
    // da aula em flight é mantido pela próxima onProgress (Android) / pela notif
    // original (iOS — onde update não re-schedula durante progresso).
    //
    // Sem este guard no Android, clicar baixar Aula B durante Aula A em flight
    // sobrescrevia o body pra "Aula B" SEM contador, causando flicker visual
    // até a próxima onProgress da A re-estabelecer "1 de 2 • Aula A • X%".
    //
    // No iOS o motivo é o mesmo hoje: a notificação de progresso não incomoda
    // mais (IOS_QUIET), mas o texto sem contador ainda apareceria por um tick.
    if (_active) return;
    _active = true;
    _lastUpdateTime = Date.now();

    if (isAndroid) {
      // Android: a FGS aceita updates idempotentes — sem spam de banner.
      // Re-chamar updateNotification reabre o mesmo card no shade, OK.
      await VxlBatchDownloader.updateNotification({
        title: NOTIF_TITLE,
        body: name || 'Preparando download...',
        progress: -1
      });
      return;
    }

    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: NOTIF_TITLE,
        body: name || 'Preparando download...',
        smallIcon: 'ic_notification',
        ongoing: true,
        ...IOS_QUIET
      }]
    });
  },

  // `position` = índice 1-based da aula em curso dentro do ciclo da fila
  // (não é "quantas terminaram"): quem chama mantém o ledger, porque a PQueue
  // tem 1 task por TURMA e o tamanho da fila não corresponde a aulas.
  async update (courseName, currentName, position, totalCount, progress) {
    if (!Capacitor.isNativePlatform()) return;
    if (!_active) return;

    _lastArgs = { courseName, currentName, progress };

    const now = Date.now();
    // Quando o total mudou (user adicionou nova aula à fila), bypassa o
    // throttle — o user clicou "baixar" e espera ver o contador subir
    // imediatamente, não em até 500ms. Atualizações regulares de progresso
    // continuam throttled.
    const totalChanged = totalCount !== _lastTotalCount;
    if (!totalChanged && now - _lastUpdateTime < MIN_UPDATE_INTERVAL_MS) return;
    _lastUpdateTime = now;
    _lastTotalCount = totalCount;

    // Título sempre genérico; corpo carrega curso + posição + aula + %.
    const parts = [];
    if (courseName) parts.push(courseName);
    // Clamp: o total é ajustado quando a turma descobre quantas aulas de fato
    // vai processar, e um ajuste pra baixo poderia deixar position > total.
    if (totalCount > 1) parts.push(`${Math.min(position, totalCount)} de ${totalCount}`);
    if (currentName) parts.push(currentName);
    if (typeof progress === 'number') parts.push(`${progress}%`);
    const body = parts.join(' • ');

    if (isAndroid) {
      // Barra é da AULA ATUAL (opção A): zera quando começa a próxima.
      await VxlBatchDownloader.updateNotification({
        title: NOTIF_TITLE,
        body,
        progress: typeof progress === 'number' ? Math.max(0, Math.min(100, progress)) : -1
      });
      return;
    }

    // iOS não tem barra de progresso em notificação (isso é Android): o avanço
    // vive no corpo, como texto. Com IOS_QUIET o re-schedule não custa banner,
    // então atualiza a cada tick — o throttle de 500ms acima é o único limite.
    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: NOTIF_TITLE,
        body,
        smallIcon: 'ic_notification',
        ongoing: true,
        ...IOS_QUIET
      }]
    });
  },

  // Republica com o total novo, reaproveitando curso/aula/progresso da última
  // update(). Chamado quando o usuário enfileira mais aulas durante um download:
  // o "X de Y" precisa reagir ao clique, não esperar o próximo tick de progresso
  // (que numa aula longa demora, e numa aula que já terminou nunca vem).
  async refreshTotals (position, totalCount) {
    if (!Capacitor.isNativePlatform()) return;
    if (!_active) return;
    return this.update(_lastArgs.courseName, _lastArgs.currentName, position, totalCount, _lastArgs.progress);
  },

  async complete (count) {
    if (!Capacitor.isNativePlatform()) return;
    _active = false;
    _lastTotalCount = 0;
    _lastArgs = { courseName: null, currentName: null, progress: null };

    const n = typeof count === 'number' && count > 0 ? count : 1;
    const body = n === 1 ? '1 aula baixada' : `${n} aulas baixadas`;

    // Android: a FGS do plugin já vai parar sozinha quando o batch terminar; aqui só
    // mostramos uma notificação one-shot "concluído" via LocalNotifications (canal
    // diferente — não interfere com a FGS).
    // iOS: mesma coisa, sem FGS.
    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: 'Download concluído',
        body,
        channelId: isAndroid ? CHANNEL_ID : undefined,
        smallIcon: 'ic_notification',
        ongoing: false,
        ...(SILENT_COMPLETE ? IOS_QUIET : {})
      }]
    });
  },

  async cancel () {
    if (!Capacitor.isNativePlatform()) return;
    _active = false;
    _lastTotalCount = 0;
    _lastArgs = { courseName: null, currentName: null, progress: null };
    await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_ID }] });
  }
};
